import { ChevronRight, Maximize2 } from 'lucide-react'
import type { Requirement } from '@ispot/shared'
import { useRequirementsViewStore } from '@/stores/requirementsViewStore'
import type { BlockMeta } from '@/hooks/useBlockReorder'
import { useInlineEditor } from '@/hooks/useInlineEditor'
import { useAutoFocus } from '@/hooks/useAutoFocus'
import { useNotesSlashCommand } from '@/hooks/useNotesSlashCommand'
import { NotesBlockControls } from '../NotesBlockControls'
import { RequirementInlineDetail } from '../RequirementInlineDetail'
import { RowDeleteButton } from '../RowDeleteButton'
import { DropIndicator } from '../DropIndicator'
import { PropertiesStrip } from './PropertiesStrip'
import type { NotesSharedProps, PersonaMeta } from './notesShared'

/**
 * Zoom-to-table: switches to Table view and asks the page to open the detail
 * drawer for this story. The page reads `pendingDetailId` from the store
 * after view change and clears it once handled.
 */
function useZoomToTable(requirementId: string) {
  const setView = useRequirementsViewStore(s => s.setView)
  const setPendingDetailId = useRequirementsViewStore(s => s.setPendingDetailId)
  return () => {
    setPendingDetailId(requirementId)
    setView('table')
  }
}

function getPrimaryPersonaMeta(r: Requirement, idx?: Map<string, PersonaMeta>): PersonaMeta | undefined {
  if (!idx) return undefined
  const pid = r.personas?.find(p => p.isPrimary)?.personaSessionId
  return pid ? idx.get(pid) : undefined
}

export function NotesStory({ requirement: r, ...shared }: NotesSharedProps & { requirement: Requirement }) {
  const expanded = useRequirementsViewStore(s => s.expandedStoryIds.has(r.id))
  const toggleExpanded = useRequirementsViewStore(s => s.toggleExpanded)
  const zoomToTable = useZoomToTable(r.id)

  const titleH = useInlineEditor({
    value: r.name,
    onCommit: (v) => shared.onUpdate(r.id, { name: v }),
    singleLine: true,
  })
  const bodyH = useInlineEditor({
    value: r.description ?? '',
    onCommit: (v) => shared.onUpdate(r.id, { description: v || null }),
  })
  const titleSlash = useNotesSlashCommand({
    value: r.name,
    onPick: async (type) => {
      if (type === 'story') return
      await shared.insertAfter(r, type, shared.personaSessionId)
    },
  })
  const bodySlash = useNotesSlashCommand({
    value: r.description ?? '',
    onPick: async (type) => {
      await shared.insertAfter(r, type, shared.personaSessionId)
    },
  })
  const autoFocusIfNew = useAutoFocus(r.id)

  const meta: BlockMeta = { type: 'story', id: r.id, parentId: r.parentId, sortOrder: r.sortOrder }
  const dragProps = shared.dnd.makeDraggableProps(meta)
  const dropProps = shared.dnd.makeDropZoneProps(meta)
  const kbProps = shared.dnd.makeKeyboardReorderProps(meta)

  const handleTitleInput: React.FormEventHandler<HTMLElement> = (e) => {
    if (titleSlash.onInput(e)) return
    titleH.onInput(e)
  }
  const handleTitleKeyDown: React.KeyboardEventHandler<HTMLElement> = (e) => {
    if (titleSlash.isOpen) return
    if (e.key === 'Enter' && !e.shiftKey) {
      titleH.onKeyDown(e)
      void shared.insertAfter(r, 'story', shared.personaSessionId)
      return
    }
    titleH.onKeyDown(e)
  }
  const handleTitleBlur: React.FocusEventHandler<HTMLElement> = (e) => {
    if (titleSlash.onBlur(e)) return
    titleH.onBlur(e)
  }
  const handleBodyInput: React.FormEventHandler<HTMLElement> = (e) => {
    if (bodySlash.onInput(e)) return
    bodyH.onInput(e)
  }
  const handleBodyKeyDown: React.KeyboardEventHandler<HTMLElement> = (e) => {
    if (bodySlash.isOpen) return
    bodyH.onKeyDown(e)
  }
  const handleBodyBlur: React.FocusEventHandler<HTMLElement> = (e) => {
    if (bodySlash.onBlur(e)) return
    bodyH.onBlur(e)
  }

  const primaryMeta = getPrimaryPersonaMeta(r, shared.personaIndex)
  const personaName = primaryMeta ? (primaryMeta.role || primaryMeta.name) : undefined

  return (
    <article
      className="group relative"
      style={{
        marginTop: 18,
        marginLeft: 16,
        paddingLeft: 14,
        borderLeft: '1px solid var(--color-warm-200)',
        opacity: shared.dnd.isDragging(meta) ? 0.4 : 1,
      }}
      data-req-id={r.id}
      {...dropProps}
    >
      <NotesBlockControls
        draggableProps={dragProps}
        keyboardReorderProps={kbProps}
        onInsert={(t) => shared.insertAfter(r, t, shared.personaSessionId)}
      />
      {shared.dnd.isDragTargetAbove(meta) && <DropIndicator />}

      {/* Title row */}
      <div className="flex items-baseline gap-2">
        <button
          onClick={(e) => { e.stopPropagation(); toggleExpanded(r.id) }}
          className="flex-shrink-0 rounded p-0.5 hover:bg-warm-100 -ml-6 mt-1"
          title={expanded ? 'Hide details' : 'Show details'}
          style={{ color: 'var(--color-fg-muted)' }}
          aria-label={expanded ? 'Hide details' : 'Show details'}
        >
          <ChevronRight size={13} style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 120ms' }} />
        </button>
        <h3
          ref={(el) => {
            ;(titleH.ref as { current: HTMLElement | null }).current = el
            autoFocusIfNew(el)
          }}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-label="Story title"
          aria-multiline="false"
          onInput={handleTitleInput}
          onKeyDown={handleTitleKeyDown}
          onPaste={titleH.onPaste}
          onBlur={handleTitleBlur}
          spellCheck={false}
          data-placeholder="Untitled story"
          className="m-0 outline-none rounded flex-1 min-w-0"
          style={{
            fontSize: 17,
            fontWeight: 600,
            letterSpacing: '-0.008em',
            lineHeight: 1.35,
            color: 'var(--color-fg)',
          }}
        >{r.name}</h3>
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); zoomToTable() }}
            aria-label="Open in Table view"
            title="Open in Table view"
            className="p-1 rounded hover:bg-warm-200"
            style={{
              color: 'var(--color-fg-muted)',
              background: 'transparent',
              border: 0,
              cursor: 'pointer',
            }}
          >
            <Maximize2 size={12} />
          </button>
          <RowDeleteButton onDelete={() => { void shared.onDelete(r.id) }} label="story" />
        </div>
      </div>

      {/* Description — same column, prose-like */}
      <div
        ref={bodyH.ref as React.RefObject<HTMLDivElement>}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-label="Story description"
        aria-multiline="true"
        onInput={handleBodyInput}
        onKeyDown={handleBodyKeyDown}
        onPaste={bodyH.onPaste}
        onBlur={handleBodyBlur}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        draggable={false}
        spellCheck={false}
        data-placeholder="Write a description…"
        className="outline-none rounded mt-1 whitespace-pre-wrap"
        style={{
          fontSize: 15,
          lineHeight: 1.55,
          color: 'var(--color-fg-secondary)',
          minHeight: '1.5em',
        }}
      />

      {/* Properties strip — single muted line, click to reveal */}
      <div className="mt-1.5">
        <PropertiesStrip requirement={r} themes={shared.themes} personaName={personaName} />
      </div>

      {titleSlash.menu}
      {bodySlash.menu}
      {expanded && (
        <div onClick={(e) => e.stopPropagation()} className="mt-3">
          <RequirementInlineDetail
            requirement={r}
            onUpdate={shared.onUpdate}
            primaryPersonaName={primaryMeta?.name}
            primaryPersonaRole={personaName}
          />
        </div>
      )}
      {shared.dnd.isDragTargetBelow(meta) && <DropIndicator />}
    </article>
  )
}
